import { React, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { DateTime } from 'luxon'
import IconButton from '@material-ui/core/IconButton'
import Badge from '@material-ui/core/Badge'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import TableRow from '@material-ui/core/TableRow'
import { MenuItem, Box, Avatar, Typography, Tooltip } from '@material-ui/core'
import DeleteLeadDialog from 'components/delete-lead-dialog'
import MarkAsContactedDialog from './mark-as-contacted-dialog'
import PersonOutlineIcon from 'components/material-symbol/icons/PersonOutline'
import PersonAddOutlinedIcon from 'components/material-symbol/icons/PersonAdd'
import DeleteIcon from 'components/material-symbol/icons/Delete'
import ChatBubble from 'components/material-symbol/icons/ChatBubble'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import CheckIcon from 'components/material-symbol/icons/Check'
import ActionsMenu from 'components/actions-menu'
import { makeStyles } from '@material-ui/styles'
import TableCell from '@material-ui/core/TableCell'
import CoachAddStudentModal from 'components/add-student-modal/coach-add-student-modal'
import { getInitials } from 'lib/text-display'
import { useIsTablet } from 'lib/hooks'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { amplitudeEvent } from 'lib/amplitude'
import { isLeadContacted } from './utils'
import { formatLeadIntent } from './lead-show-page/utils'

export const useStyles = makeStyles(theme => ({
  leadRow: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  leadDetailsLink: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
  leadText: {
    color: theme.palette.text.primary,
  },
  unviewedLeadText: {
    color: theme.palette.error.dark,
  },
  leadNameCell: {
    borderTop: '1px solid #EEEEEE !important',
    borderLeft: '1px solid #EEEEEE !important',
    borderBottom: '1px solid #EEEEEE !important',
    borderRadius: '8px 0 0 8px!important',
    paddingLeft: '1.5rem',
  },
  row: {
    cursor: 'pointer',
  },
  nameTableCell: {
    overflow: 'visible',
    minWidth: 0,
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    minWidth: 0,
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
    },
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    paddingLeft: theme.spacing(0.75),
  },
  avatarBadge: {
    flexShrink: 0,
    overflow: 'visible',
  },
  studentAvatar: {
    height: '36px',
    width: '36px',
    flexShrink: 0,
    backgroundColor: '#EBF0F2',
    color: '#172024',
    fontSize: '13px',
    fontWeight: 500,
  },
  text: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    borderRadius: 4,
    color: '#6B7C87',
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  link: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  icon: {
    color: theme.palette.grey[600],
    fontSize: '1.25rem',
  },
  badge: {
    left: '-0.75rem',
    top: '50%',
    backgroundColor: theme.palette.blue.main,
  },
}))

export const LeadTableItem = ({
  lead,
  contactMethodOptions = [],
  refetch,
  studentRefetch,
  showRespondButton = false,
  showRelativeTime = false,
}) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const history = useHistory()
  const timeToShowWarning = 1000 * 60 * 60 * 24 * 7 // 1 week

  const { contact = {} } = lead
  const leadName = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()

  const [
    isConvertLeadToStudentModalOpen,
    setIsConvertLeadToStudentModalOpen,
  ] = useState(false)

  const [isDeleteLeadDialogOpen, setIsDeleteLeadDialogOpen] = useState(false)

  const [
    isMarkAsContactedDialogOpen,
    setIsMarkAsContactedDialogOpen,
  ] = useState(false)

  const convertLeadToStudentButton = () => {
    amplitudeEvent('convert-to-student-clicked', {
      leadId: lead.id,
      url: window.location.href,
      path: window.location.pathname,
    })
    setIsConvertLeadToStudentModalOpen(true)
  }

  const openDeleteLeadDialog = event => {
    event.stopPropagation()
    setIsDeleteLeadDialogOpen(true)
  }

  const openMarkAsContactedDialog = event => {
    event.stopPropagation()
    setIsMarkAsContactedDialogOpen(true)
  }

  const closeMarkAsContactedDialog = () => {
    setIsMarkAsContactedDialogOpen(false)
  }

  const refetchLeadsAndStudents = () => {
    refetch()
    studentRefetch?.()
  }

  const styleLeads = () => {
    let str = ''
    const submittedTime = DateTime.fromISO(lead.createdAt).toMillis()
    const now = DateTime.now().toMillis()
    if (!isLeadContacted(lead) && now - submittedTime > timeToShowWarning) {
      str += `${classes.unviewedLeadText}`
    }
    return str
  }

  return (
    <>
      <TableRow
        className={classes.row}
        onClick={() => history.push(`/pga-coach/leads/${lead.id}`)}
      >
        <TableCell className={classes.nameTableCell}>
          <Box className={classes.nameCell}>
            <Badge
              variant="dot"
              badgeContent={!lead.viewedAt && !isLeadContacted(lead) ? ' ' : 0}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              classes={{ badge: classes.badge, root: classes.avatarBadge }}
            >
              <Avatar className={classes.studentAvatar}>
                {getInitials(leadName)}
              </Avatar>
            </Badge>
            <Box className={classes.info}>
              {isTablet && (
                <Typography className={`${classes.text} ${styleLeads()}`}>
                  Submitted:{' '}
                  {showRelativeTime
                    ? DateTime.fromISO(lead.createdAt).toRelative()
                    : DateTime.fromISO(lead.createdAt).toLocaleString(
                      DateTime.DATE_MED,
                    )}
                </Typography>
              )}
              <Typography className={classes.text} noWrap>
                {leadName}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        {!isTablet && (
          <TableCell className={styleLeads()}>
            {showRelativeTime
              ? DateTime.fromISO(lead.createdAt).toRelative()
              : DateTime.fromISO(lead.createdAt).toLocaleString(
                DateTime.DATE_MED,
              )}
          </TableCell>
        )}
        {!isTablet && <TableCell>{formatLeadIntent(lead.intent)}</TableCell>}
        <TableCell
          className={classes.actions}
          onClick={event => event.stopPropagation()}
        >
          <Box className={classes.actionButtons}>
            {contactMethodOptions.length > 0 && (
              <Tooltip title="Mark as contacted">
                <IconButton
                  aria-label="Mark as contacted"
                  className={classes.borderedActionButton}
                  onClick={openMarkAsContactedDialog}
                >
                  <CheckIcon fontSize="small" fill={0} />
                </IconButton>
              </Tooltip>
            )}
            {showRespondButton && (
              <Tooltip title="Respond">
                <IconButton
                  aria-label="Message lead"
                  className={classes.borderedActionButton}
                  onClick={event => {
                    event.stopPropagation()
                    history.push(`/pga-coach/leads/${lead.id}`)
                  }}
                >
                  <ChatBubble fontSize="small" fill={0} />
                </IconButton>
              </Tooltip>
            )}
            <ActionsMenu
              ariaId={`lead-actions-${lead.id}`}
              aria-label="Lead actions"
              data-testid={`lead-list-actions-menu-${lead.id}`}
              buttonContent={<MoreHoriz fontSize="small" />}
              className={classes.plainActionButton}
              color="default"
            >
              <Link
                to={`/pga-coach/leads/${lead.id}`}
                className={classes.leadDetailsLink}
              >
                <MenuItem>
                  <ListItemIcon>
                    <PersonOutlineIcon className={classes.icon} />
                  </ListItemIcon>
                  <ListItemText primary="View Lead Details" />
                </MenuItem>
              </Link>
              <MenuItem onClick={convertLeadToStudentButton}>
                <ListItemIcon>
                  <PersonAddOutlinedIcon className={classes.icon} />
                </ListItemIcon>
                <ListItemText primary="Convert to Student" />
              </MenuItem>
              <SensitiveInfoWrapper>
                <MenuItem
                  onClick={openDeleteLeadDialog}
                  data-testid="delete-lead"
                >
                  <ListItemIcon>
                    <DeleteIcon className={classes.icon} />
                  </ListItemIcon>
                  <ListItemText primary="Delete Lead" />
                </MenuItem>
              </SensitiveInfoWrapper>
            </ActionsMenu>
          </Box>
        </TableCell>
      </TableRow>
      {isConvertLeadToStudentModalOpen && (
        <CoachAddStudentModal
          open={isConvertLeadToStudentModalOpen}
          setOpen={setIsConvertLeadToStudentModalOpen}
          onAdd={refetchLeadsAndStudents}
          initialValues={{
            studentFirstName: lead.firstName,
            studentLastName: lead.lastName,
            email: lead.contact?.email,
            phoneNumber: lead.contact?.phone,
          }}
          leadId={lead.id}
        />
      )}
      {isDeleteLeadDialogOpen && (
        <DeleteLeadDialog
          lead={lead}
          open={isDeleteLeadDialogOpen}
          setOpen={setIsDeleteLeadDialogOpen}
          refetch={refetch}
        />
      )}
      {isMarkAsContactedDialogOpen && (
        <MarkAsContactedDialog
          open={isMarkAsContactedDialogOpen}
          onClose={closeMarkAsContactedDialog}
          contactMethodOptions={contactMethodOptions}
          lead={lead}
          refetch={refetch}
        />
      )}
    </>
  )
}
